import { useCallback } from 'react'
import { useParams } from 'react-router-dom'

import { useMutation } from '@apollo/client'
import {
  ExpensablesDataTableDocument,
  MyGroupedExpensablesDataTableDocument,
} from 'generated/resources/datatable.graphql'
import {
  CreateGermanyPerDiemDocument,
  CreateGermanyPerDiemMutation,
  CreateGermanyPerDiemMutationVariables,
  ExpensablesDataCollectionDocument,
  ExpensablesStatusCountsDocument,
  GetExpensesCountDocument,
  GetGroupsDocument,
  MyGroupedExpensablesDataCollectionDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { generateDefaultTripName } from 'modules/expenses/components/expenses/New/PerDiemWizard/generateDefaultTripName'
import i18n from 'modules/core/lib/i18n'

import {
  type DayExclusion,
  type Destination,
  mapCostCentersForMutation,
  type PerDiemWizardData,
} from 'modules/expenses/components/expenses/New/PerDiemWizard/form/types'

interface CreatePerDiemResult {
  success: boolean
  expensableId?: string
  perDiemId?: string
  error?: string
}

interface UseCreateGermanyPerDiemReturn {
  createPerDiem: (data: PerDiemWizardData, companyId: string) => Promise<CreatePerDiemResult>
  isCreating: boolean
}

const formatDateToISO = (date: Date | null): string | undefined => {
  if (!date) return undefined
  return date.toISOString()
}

const formatDateToISODate = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`
}

const mapDestinations = (destinations: Destination[]) => {
  return destinations
    .filter((dest): dest is Destination & { arrivalDatetime: Date } =>
      Boolean(dest.locationData.country && dest.locationName && dest.arrivalDatetime)
    )
    .map((dest) => {
      const { arrivalDatetime } = dest
      return {
        locationName: dest.locationName,
        arrivalDate: arrivalDatetime.toISOString(),
        country: dest.locationData.country,
        administrativeAreaLevel1: dest.locationData.administrativeAreaLevel1,
        administrativeAreaLevel2: dest.locationData.administrativeAreaLevel2,
        locality: dest.locationData.locality,
      }
    })
}

const mapConcepts = (dayExclusions: DayExclusion[], endDate: Date | null) => {
  const lastDate = endDate ? formatDateToISODate(endDate) : null
  return dayExclusions.map((exclusion) => {
    const date = formatDateToISODate(exclusion.date)
    return {
      date,
      breakfastProvided: exclusion.breakfastProvided,
      lunchProvided: exclusion.lunchProvided,
      dinnerProvided: exclusion.dinnerProvided,
      accommodationProvided: date === lastDate ? false : exclusion.accommodationProvided,
    }
  })
}

const mapProjects = (projectId?: string, subprojectId?: string) => {
  if (!projectId) return undefined

  return [
    {
      projectWorkerId: projectId,
      subprojectId: subprojectId ?? undefined,
    },
  ]
}

export const useCreateGermanyPerDiem = (): UseCreateGermanyPerDiemReturn => {
  const { employee } = useCurrentSession()
  const { groupId } = useParams()

  const [mutate, { loading }] = useMutation<
    CreateGermanyPerDiemMutation,
    CreateGermanyPerDiemMutationVariables
  >(CreateGermanyPerDiemDocument, {
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      ExpensablesStatusCountsDocument,
      GetExpensesCountDocument,
      GetGroupsDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
    ],
    awaitRefetchQueries: true,
  })

  const createPerDiem = useCallback(
    async (data: PerDiemWizardData, companyId: string): Promise<CreatePerDiemResult> => {
      try {
        const destinations = mapDestinations(data.destinations)

        if (destinations.length === 0) {
          return {
            success: false,
            error: i18n.t('expenses_v2.forms.germany_per_diem_modal.errors.destination_required'),
          }
        }

        const projects = mapProjects(data.projectId, data.subprojectId)

        const result = await mutate({
          variables: {
            companyId,
            employeeId: data.employeeId ?? employee.id,
            description: data.description,
            tripName:
              data.tripName ||
              generateDefaultTripName(data.destinations, data.startDate, data.endDate) ||
              null,
            startDate: formatDateToISO(data.startDate),
            endDate: formatDateToISO(data.endDate),
            destinations,
            concepts: mapConcepts(data.dayExclusions, data.endDate),
            files: data.files,
            budgetId: data.budgetId,
            groupId: groupId ?? null,
            internalReference: data.internalReference,
            projects,
            costCenters: mapCostCentersForMutation(data.costCenters),
            currency: 'EUR',
          },
        })

        const expensable = result.data?.expensesMutations?.expensable

        if (!expensable?.expensable) {
          const errors = expensable?.errors
          const firstError = errors?.[0]
          const errorMessage =
            (firstError && 'message' in firstError ? firstError.message : null) ??
            i18n.t('expenses_v2.forms.germany_per_diem_modal.errors.create_failed')
          return {
            success: false,
            error: errorMessage,
          }
        }

        return {
          success: true,
          expensableId: expensable.expensable.id,
          perDiemId: expensable.expensable.perDiem?.id,
        }
      } catch (error) {
        return {
          success: false,
          error:
            error instanceof Error
              ? error.message
              : i18n.t('expenses_v2.forms.germany_per_diem_modal.errors.unknown_error'),
        }
      }
    },
    [mutate, employee.id, groupId]
  )

  return {
    createPerDiem,
    isCreating: loading,
  }
}
